import {
  ALWAYS_AI_ROLE_PERMISSIONS,
  type Permission,
} from "@ndd/core-data/permissions";
import type { AlwaysAiRole, AlwaysAiUser } from "../../../shared/api-types";

type RoleSubject =
  | AlwaysAiRole
  | (Pick<AlwaysAiUser, "role"> & {
      roles?: readonly AlwaysAiRole[];
    });

function rolesFor(subject: RoleSubject): readonly AlwaysAiRole[] {
  if (typeof subject === "string") return [subject];

  return subject.roles ?? [subject.role];
}

export function hasRole(subject: RoleSubject, role: AlwaysAiRole): boolean {
  return rolesFor(subject).includes(role);
}

export function can(subject: RoleSubject, permission: Permission): boolean {
  return rolesFor(subject).some((role) =>
    ALWAYS_AI_ROLE_PERMISSIONS[role].includes(permission)
  );
}

/** Either admin tier: the gate on the `/admin/*` pages, whose server routes
 *  each check their own permission. */
export function isAlwaysAiAdmin(subject: RoleSubject): boolean {
  return hasRole(subject, "admin") || hasRole(subject, "clinical_admin");
}

/**
 * May this viewer name any RD as an Appointment read target (NO-467)? Separate
 * from `canReadRdWorkspace`: a supervisor reaches sessions but not schedules.
 */
export function canSelectAppointmentRd(subject: RoleSubject): boolean {
  return can(subject, "appointments:read:all");
}

/**
 * May this viewer open RD Hours at all (NO-878)? Own, supervised, or any RD's.
 * The server decides per RD; this only decides whether the Availability tab exists.
 */
export function canManageRdHours(subject: RoleSubject): boolean {
  return (
    can(subject, "rd_hours:manage:own") ||
    can(subject, "rd_hours:manage:supervised") ||
    can(subject, "rd_hours:manage:all")
  );
}

/** Any Appointment reach at all, either rung. Mirrors the server router gate. */
export function canReadAppointments(subject: RoleSubject): boolean {
  return can(subject, "appointments:read:own") || canSelectAppointmentRd(subject);
}

export function canReadRdWorkspace(subject: RoleSubject): boolean {
  return can(subject, "rds:read:all") || can(subject, "rds:read:supervised");
}

/**
 * May this viewer rename an RD? `PATCH /api/rds/:id` authorizes through
 * `authorizeRdRead`, so renaming needs exactly the RD-workspace role rung.
 * The per-RD supervisor-ownership half of that gate stays on the server: it is
 * not restated here because `GET /api/rds/:id` applies the same check, so a
 * viewer who fails it never reaches a header to rename from. Named separately
 * from `canReadRdWorkspace` so a narrower edit permission changes one line.
 */
export function canRenameRd(subject: RoleSubject): boolean {
  return canReadRdWorkspace(subject);
}

// "/rds" exact is included so the shell redirects before any legacy chrome
// or the RDs list can flash for roles that land elsewhere.
export function shouldRedirectFromRdWorkspace(
  subject: RoleSubject,
  pathname: string
): boolean {
  return (
    !canReadRdWorkspace(subject) &&
    (pathname === "/rds" || pathname.startsWith("/rds/"))
  );
}

// The rd-home flag dark-launches the whole RD Home experience: with it off,
// RDs land on upload in the legacy chrome exactly as before NO-412, and the
// Appointment routes bounce them there too.
export function shouldRedirectFromAppointments(
  subject: RoleSubject,
  pathname: string,
  rdHomeEnabled: boolean
): boolean {
  if (isSessionDraftPath(pathname)) {
    return !can(subject, "soap_drafts:read:own") || !rdHomeEnabled;
  }

  if (!isAppointmentPath(pathname)) return false;

  // Admin reach rides the same dark launch as the RD's: one flag, one surface.
  return !canReadAppointments(subject) || !rdHomeEnabled;
}

export function landingPathForRole(
  subject: RoleSubject,
  rdHomeEnabled: boolean
): "/appointments" | "/rds" | "/transcripts/upload" {
  const role = typeof subject === "string" ? subject : subject.role;
  if (role === "rd") return rdHomeEnabled ? "/appointments" : "/transcripts/upload";

  if (role === "va") return "/transcripts/upload";

  return "/rds";
}

// The surfaces the RD shell owns — exactly the RD-reachable routes.
export function isRdShellPath(pathname: string): boolean {
  const RD_SHELL_EXACT_PATHS = ["/appointments", "/transcripts/upload"];

  return (
    RD_SHELL_EXACT_PATHS.includes(pathname) ||
    pathname.startsWith("/appointments/") ||
    isSessionDraftPath(pathname)
  );
}

// RDs get the shell everywhere they reach. Anyone else with Appointment reach —
// a supervisor or clinical admin on their own schedule, an admin reading an
// RD's (NO-467) — gets it on Appointments and on their own SOAP Drafts, where
// the design mirrors the RD's Home so support sees what the RD sees.
export function usesRdShell(
  subject: RoleSubject,
  pathname: string,
  rdHomeEnabled: boolean
): boolean {
  if (!rdHomeEnabled) return false;

  if (hasRole(subject, "rd") && isRdShellPath(pathname)) return true;

  if (isSessionDraftPath(pathname)) return can(subject, "soap_drafts:read:own");

  return canReadAppointments(subject) && isAppointmentPath(pathname);
}

export function isAppointmentPath(pathname: string): boolean {
  return pathname === "/appointments" || pathname.startsWith("/appointments/");
}

export function isSessionDraftPath(pathname: string): boolean {
  return /^\/sessions\/[^/]+\/draft$/.test(pathname);
}

/**
 * Is the viewer reading their OWN Appointment rows? Drives every chrome
 * divergence — first-person copy, the Soon items, the upload affordance.
 *
 * Keyed on which rows are read rather than the role, because a dual rd+admin
 * holder reads their own Home with no selection and a colleague's with one.
 */
export function readsOwnAppointmentRows(
  subject: RoleSubject,
  rdId: string | undefined
): boolean {
  if (rdId !== undefined) return !canSelectAppointmentRd(subject);

  return can(subject, "appointments:read:own");
}
